import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnInit } from '@angular/core';
import { Component, Inject, Input, PLATFORM_ID, Renderer2, ViewChild, inject } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import type { Image } from '@core-mkt/interfaces/image';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { ImagePreloadDirective } from '@core-mkt/services/image-preload/image-preload.directive';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { ProductService } from '@core-mkt/services/product/product.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import type { DropdownData } from '@core-mkt/shared/components/vision-dropdown/vision-dropdown/vision-dropdown';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import type { ButtonsLayoutItems, DropdownLayoutItems, ProductPicker } from './layout';
import { Layout } from './layout';

import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { BrandConfigurationService } from '@core-mkt/services/configuration/brand-configuration.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ShortcodeDataContextService } from '@core-mkt/services/shortcode-data-context/shortcode-data-context.service';
import { TrustpilotService } from '@core-mkt/services/trustpilot/trustpilot.service';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { ProductPickerComponent } from '../../product-picker/product-picker/product-picker.component';
import { VisionButtonComponent } from '../../vision-button/vision-button/vision-button.component';
import { VisionDropdownComponent } from '../../vision-dropdown/vision-dropdown/vision-dropdown.component';
import { VisionModalService } from '../../vision-modal/vision-modal.service';

@Component({
  selector: 'layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ProductPickerComponent,
    VisionDropdownComponent,
    VisionButtonComponent,
    ImagePreloadDirective,
    LazyImgDirective,
  ],
})
export class LayoutComponent implements OnInit, AfterViewInit {
  @Input() data: Layout;
  @ViewChild('layout') layout;
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  theme: ThemeInterface;
  faChevronDown = faChevronDown;
  useLazyLoad = false;
  faTimes = faTimes;
  buttonsData: VisionButton[] = [];
  buttonWidth: string;
  tertiaryLayout: string;
  dropdownAlignment: string;
  modalContent: string;
  showModal = false;
  trustpilotTrustScore: number;
  trustpilotNumberOfReviews: number;
  trustpilotVerified = false;
  trustpilotBrandUrl: string;

  constructor(
    private redactor: WysiwygRedactorService,
    private ps: ProductService,
    private themeParser: ThemeParserService,
    private imgixService: ImgixService,
    private tps: TrustpilotService,
    private bcs: BrandConfigurationService,
    private env: EnvService,
    private modal: VisionModalService,
    private shortcodeDataContext: ShortcodeDataContextService,
    private accessibilityService: AccessibilityService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.trustpilotBrandUrl = `https://www.trustpilot.com/review/${this.bcs.url}`;
  }

  private injectPreloadLink(src: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('href', src);
    this.renderer.appendChild(this.document.head, link);
  }

  get image(): Image {
    if (this.data.image && !!this.data.image[0]) {
      return this.data.image[0];
    }
    return null;
  }

  get imageUrl(): string {
    if (this.data.image && !!this.data.image[0]) {
      if (this.data.image[0].url.includes('auto=compress,format&w=534')) {
        return this.data.image[0].url;
      }
      return `${this.data.image[0].url}&auto=compress,format&w=534`;
    }
    return null;
  }

  get contentCardTitle(): string | undefined {
    return this.data.contentCardTitle;
  }

  get layoutTitle(): SafeHtml {
    return this.data.layoutTitle;
  }

  get layoutContent(): SafeHtml {
    return this.data.layoutContent;
  }

  get layoutRightContent(): SafeHtml {
    return this.data.layoutRightContent;
  }

  get dropdownTopLabel(): string {
    return this.data.dropdownTopLabel;
  }

  get dropdownPlaceholder(): string {
    return this.data.dropdownPlaceholder;
  }

  get dropdownButtonType(): string {
    return this.data.dropdownButtonType;
  }

  get dropdown(): DropdownLayoutItems[] {
    return this.data.dropdown;
  }

  get dropdownData(): DropdownData {
    return {
      dropdownItems: this.dropdown,
      dropdownTopLabel: this.dropdownTopLabel,
      dropdownPlaceholder: this.dropdownPlaceholder,
      dropdownAlignment: '',
    };
  }

  get buttons(): VisionButton[] {
    return this.buttonsData;
  }

  get layoutSplit(): boolean {
    return this.data.layoutSplit;
  }

  get hideStandaloneProductPicker(): boolean {
    return this.data.hideStandaloneProductPicker;
  }

  get buttonLayout(): string {
    if (this.buttons?.length > 3 || (this.buttons?.length > 2 && this.dropdown?.length > 0)) {
      return 'flex flex-row flex-wrap justify-center';
    }
    return this.data.buttonLayout;
  }

  get buttonAlignment(): string {
    if (this.productPickerContent) {
      return 'justify-center';
    }
    return this.buttons.length > 3 ? '' : this.data.buttonAlignment;
  }

  get hasButtonRow(): boolean {
    return this.data.buttonLayout !== null && this.data.buttonLayout === 'grid grid-cols-2';
  }

  get isCard(): boolean {
    return !!this.data.cardType;
  }

  get isCardContent(): boolean {
    return this.data.cardType === 'card';
  }

  get cardImageStyle(): string {
    if (this.data.cardImage && !!this.data.cardImage[0]) {
      return `url(${this.data.cardImage[0].url})`;
    }
    return '';
  }

  get cardContent(): SafeHtml {
    return this.data.cardContent;
  }

  get cardButtonType(): string {
    return this.data.cardButtonType;
  }

  get cardButtonLabel(): string {
    return this.data.cardButtonLabel;
  }

  /**
   * Gets the content of the product picker.
   * @returns The first element of the productPicker array from Craft
   */
  get productPickerContent(): ProductPicker[] {
    if (
      this.data.productPickerTable &&
      this.data.productPickerTable.length > 0 &&
      this.data.productPickerTable[0].productPickerTable
    ) {
      return this.data.productPickerTable;
    }
  }

  get cardButtonData(): VisionButton {
    return {
      bgTheme: this.data.backgroundColor,
      buttonLink: this.data.cardButtonLink,
      buttonText: this.data.cardButtonLabel,
      buttonStyle: this.data.cardButtonType,
      newTab: this.data.cardButtonLinkNewTab,
      nofollow: this.data.cardButtonLinkNofollow,
      noopener: this.data.cardButtonLinkNewTab,
      noreferrer: this.data.cardButtonLinkNewTab,
      fullWidth: false,
      useIcon: true,
      ctaTag: this.data.cardButtonCtaTag,
    };
  }

  get flexClass(): string {
    if (this.image || this.layoutRightContent) {
      /* TODO: Remove hack after V2
       * There are several imageAligment fields in the single template. Standardize them to use the same Labels/Values
       */
      if (this.data.imageAlignment.startsWith('flex-col')) {
        //V2 Version
        return this.data.imageAlignment.replace('md:', 'lg:');
      } else {
        //V3 Version, need this for a layout fix
        return `flex-col-reverse lg:${this.data.imageAlignment}`;
      }
    }
    if (this.isCard) {
      return this.data.cardAlignment;
    }
    return '';
  }

  ngOnInit(): void {
    if (this.data.index > 2) {
      this.useLazyLoad = true;
    }
    this.dropdownAlignment = this.data.layoutContent?.toString().match(/text-align:\s*right/)
      ? 'float-right'
      : this.data.layoutContent?.toString().match(/text-align:\s*center/)
      ? undefined
      : 'float-left';
    this.data.contentCardTitle = this.redactor.bypassSanitizer(this.data.contentCardTitle ?? null);
    this.data.layoutTitle = this.redactor.bypassSanitizer(this.data.layoutTitle);
    const cardBypass = this.useLazyLoad ? 'lazy' : 'lcp';
    if (!this.useLazyLoad && !this.isBrowser) {
      const src = this.redactor.extractFirstImageSrc(this.data.cardContent);
      if (src) this.injectPreloadLink(src);
    }
    this.data.cardContent = this.redactor.bypassSanitizer(this.data.cardContent, cardBypass);
    const paddingWidth = 5.5;
    const maxValue = Math.max(...this.buttonsData.map((b) => b.buttonText.length));
    if (this.buttonsData.length === 2) {
      const minValue = Math.min(...this.buttonsData.map((b) => b.buttonText.length));
      const difference = maxValue - minValue;
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      this.buttonWidth = (difference < paddingWidth ? maxValue + difference - 1.5 : maxValue - 1) + paddingWidth + 'ch';
    } else if (this.buttonsData.length > 2) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      this.buttonWidth = maxValue + paddingWidth + 'ch';
    }
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: this.data.textColor,
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
    this.createButtonsDataList();

    // Set up product picker data in context for shortcode usage
    if (this.productPickerContent) {
      this.shortcodeDataContext.setProductPickerData('default', this.productPickerContent);
      // Also allow accessing by component index if needed
      this.shortcodeDataContext.setProductPickerData(`layout-${this.data.index || 0}`, this.productPickerContent);
    }

    if (this.data.showTrustpilot && this.env.get.brandConfig.trustpilotId.length > 0) {
      void this.tps.getBrandStarsRating().then((trustPilotRating) => {
        if (!trustPilotRating) return;
        this.trustpilotTrustScore = trustPilotRating.score.trustScore;
        this.trustpilotNumberOfReviews = trustPilotRating.numberOfReviews.total;
        this.checkTrustpilotStars();
      });
    }

    // Inject preload link server-side only — ngOnInit runs again on client during hydration
    if (!this.useLazyLoad && !this.isBrowser) {
      const src =
        this.redactor.extractFirstImageSrc(this.data.layoutContent) ??
        this.redactor.extractFirstImageSrc(this.data.layoutRightContent);
      if (src) this.injectPreloadLink(src);
    }

    // SSR: pre-process shortcodes synchronously so ShortcodeRendererDirective can replace
    // placeholder divs during ngAfterViewInit. In the browser, this is handled via
    // MutationObserver after bypassSanitizer() runs asynchronously in ngAfterViewInit().
    if (!this.isBrowser) {
      if (this.data.layoutContent) {
        this.data.layoutContent = this.redactor.parseShortcodesOnly(this.data.layoutContent);
      }
      if (this.data.layoutRightContent) {
        this.data.layoutRightContent = this.redactor.parseShortcodesOnly(this.data.layoutRightContent);
      }
    }
  }

  async ngAfterViewInit(): Promise<void> {
    // Wait for nav links to be loaded before processing anchor tags
    await this.accessibilityService.waitForNavLinks();
    const bypass = this.useLazyLoad ? 'lazy' : 'lcp';
    this.data.layoutContent = this.redactor.bypassSanitizer(this.data.layoutContent, bypass);
    this.data.layoutRightContent = this.redactor.bypassSanitizer(this.data.layoutRightContent, bypass);
  }

  activateModal(idx: number): void {
    idx = idx + 1;
    if (idx < 3 && (this.data.highlightedTextModal1.length > 0 || this.data.highlightedTextModal2.length > 0)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const modalContent = this.data[`highlightedTextModal${idx}`][0].modalContent;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.modal.openModal({ showModal: true, modalContent });
    }
  }

  modalHasContent(idx: number): boolean {
    idx = idx + 1;
    const modal = this.data[`highlightedTextModal${idx}`] as unknown[];
    return modal.length > 0;
  }

  isModalButton(button: VisionButton): boolean {
    return button.buttonLink.includes('/useModal');
  }

  modalWrapperClickHandler(event: Event): void {
    if (
      (event.target as HTMLElement).classList.contains('highlighted-text-modal-1') &&
      this.data.highlightedTextModal1.length > 0
    ) {
      const modalContent = this.data.highlightedTextModal1[0].modalContent;
      this.modal.openModal({ showModal: true, modalContent });
    }
    if (
      (event.target as HTMLElement).classList.contains('highlighted-text-modal-2') &&
      this.data.highlightedTextModal2.length > 0
    ) {
      const modalContent = this.data.highlightedTextModal2[0].modalContent;
      this.modal.openModal({ showModal: true, modalContent });
    }
  }

  getButtonClasses(isLast: boolean): string {
    if (!this.buttonLayout.includes('grid') && !this.buttonLayout.includes('flex-row')) {
      return isLast ? 'mx-auto' : 'block mb-3 mx-auto';
    }
    return isLast ? 'block mb-3 mt-1.5' : 'block mb-3 mt-1.5 mr-2';
  }

  getButtonSectionClasses(): string {
    let widthClass = 'w-full';
    if (!this.buttonLayout.includes('flex-row') && !this.productPickerContent) {
      widthClass = 'w-max';
    }
    return this.productPickerContent && this.layoutSplit ? 'lg:w-[40%] lg:pl-v-spacing' : `${widthClass} pt-8 flex`;
  }

  private checkTrustpilotStars(): void {
    if (this.trustpilotNumberOfReviews) {
      this.trustpilotVerified = true;
    }
  }

  openTrustpilot(): void {
    this.tps.openTrustpilot(this.trustpilotBrandUrl);
  }

  /**
   * Adding buttonsData with aria label value
   * @param button vision button information
   */
  addButtonsData(button: ButtonsLayoutItems): void {
    const buttonData: VisionButton = {
      bgTheme: this.theme.root,
      buttonLink: button.buttonLink,
      buttonText: button.buttonLabel,
      buttonAriaLabel: button.buttonLinkAriaLabel,
      buttonStyle: button.buttonType,
      newTab: button.buttonLinkNewTab,
      nofollow: button.buttonLinkNofollow,
      noopener: button.buttonLinkNewTab,
      noreferrer: button.buttonLinkNewTab,
      fullWidth: false,
      ctaTag: button.buttonCtaTag,
    };
    this.buttonsData.push(buttonData);
  }

  /**
   * Getting aria label from product title
   * valitating to get aria label according url link
   */
  createButtonsDataList(): void {
    const buttons = this.data.buttons ? this.data.buttons : this.data.aarpButtons;
    buttons?.forEach((button) => {
      if (button.buttonLink?.startsWith('https://checkout')) {
        const linkList = button.buttonLink?.split('?');
        const queryString = linkList[linkList.length - 1];
        const urlParams = new URLSearchParams(queryString);
        const productId = urlParams.get('productId');
        const productEntry: CraftXGritProductEntry = {
          xgritProductId: productId,
        };

        void this.ps.getProductData(productEntry).then((product: XGritCompleteProduct) => {
          button.buttonLinkAriaLabel = product.apiData.title ? product.apiData.title : undefined;
          this.addButtonsData(button);
        });
      } else {
        this.addButtonsData(button);
      }
    });
  }
}
